/**
 * JSON-safe serialization for ECharts config values that are functions (e.g. `animationDelay`,
 * conditional `label.formatter`). Functions are replaced with `{ $fn: "<source>" }` markers so
 * the config survives JSON persistence; on the receiving end, markers are rehydrated back into
 * live functions before the config is handed to ECharts.
 *
 * Supported function shapes: arrow functions and function expressions. Method shorthand
 * (`formatter(params) { ... }`) is not supported because it is not a valid standalone expression.
 *
 * Only pure functions round-trip correctly — `toString()` loses any closure bindings, so the
 * function source must reference only its own arguments and globals.
 */

export const FN_MARKER = '$fn';

export type SerializedFunction = { [FN_MARKER]: string };

export function isSerializedFunction(value: unknown): value is SerializedFunction {
    return (
        typeof value === 'object' &&
        value !== null &&
        !Array.isArray(value) &&
        FN_MARKER in value &&
        typeof (value as Record<string, unknown>)[FN_MARKER] === 'string'
    );
}

export function serializeFunctions<T>(input: T): T {
    return walk(input, serializeNode) as T;
}

export function deserializeFunctions<T>(input: T): T {
    return walk(input, deserializeNode) as T;
}

function serializeNode(node: unknown): unknown | typeof CONTINUE {
    if (typeof node === 'function') {
        return { [FN_MARKER]: (node as (...args: unknown[]) => unknown).toString() };
    }
    return CONTINUE;
}

function deserializeNode(node: unknown): unknown | typeof CONTINUE {
    if (isSerializedFunction(node)) {
        // eslint-disable-next-line no-new-func
        return new Function('return (' + node[FN_MARKER] + ')')();
    }
    return CONTINUE;
}

const CONTINUE = Symbol('continue');

function walk(node: unknown, visit: (node: unknown) => unknown | typeof CONTINUE): unknown {
    const replaced = visit(node);
    if (replaced !== CONTINUE) return replaced;

    if (Array.isArray(node)) {
        return node.map((item) => walk(item, visit));
    }
    if (node && typeof node === 'object') {
        const result: Record<string, unknown> = {};
        for (const key in node as Record<string, unknown>) {
            result[key] = walk((node as Record<string, unknown>)[key], visit);
        }
        return result;
    }
    return node;
}
